<i18n lang="yaml">
en:
  edit: Edit
  name: Name
  save: Save
  organization_updated: Organization updated
  delete_organization_header: Delete organization
  delete_organization_body: 'Warning: this action is irreversible!'
  delete_organization_confirmation: Are you sure you want to delete this organization? This action is irreversible!
  delete: Delete
  cancellable_help: 'Cancel your plan to remove organization'
ru:
  edit: Редактировать
  name: Название
  save: Сохранить
  organization_updated: Организация обновлена
  delete_organization_header: Удалить организацию
  delete_organization_body: 'Внимание: это действие невозможно отменить!'
  delete_organization_confirmation: Вы уверены, что хотите удалить эту организацию? Это действие невозможно отменить!
  delete: Удалить
  cancellable_help: 'Отмените свой план, чтобы удалить организацию'
</i18n>

<script setup lang="ts">
import { Form, Field } from 'vee-validate'
import * as yup from 'yup'
import { useMyFetch } from '@/utils'
import { useAllStores } from '@/store'
import { handleError } from '@/utils/errors'
import { computed } from 'vue'

const { organizationStore, alertStore, authStore, router, t, route } = useAllStores()

const values = {
  name: ''
}
const { data, error } = await useMyFetch(`organizations/${route.params.organization_id}`).json()
await handleError(error, router, authStore, alertStore, 'organizations:list')
values.name = data.value.name

const schema = yup.object({
  name: yup.string().required()
})

async function onSubmit() {
  const { data, error } = await useMyFetch(`organizations/${route.params.organization_id}`)
    .post(values)
    .json()
  if (error.value) {
    await handleError(error, router, authStore, alertStore)
  } else {
    organizationStore.selectOrganization(data.value.id, data.value.name)
    alertStore.success(t('organization_updated'))
  }
}

async function onDelete() {
  if (confirm(t('delete_organization_confirmation'))) {
    const { error } = await useMyFetch(`organizations/${route.params.organization_id}`)
      .json()
      .delete()
    if (error.value) {
      await handleError(error, router, authStore, alertStore)
    } else {
      alertStore.success('Organization deleted')
      if (organizationStore.organization.id === route.params.organization_id) {
        organizationStore.clearOrganization()
      }
      await router.push({ name: 'organizations:list' })
    }
  }
}

const shouldDisableDeleteButton = computed(() => {
  return data.value.plan.price !== 0
})
</script>

<template>
  <div class="card">
    <h4 class="card-header">{{ t('edit') }}</h4>
    <div class="card-body">
      <Form @submit="onSubmit" :validation-schema="schema" v-slot="{ errors, isSubmitting }">
        <div class="form-group mb-3">
          <label>{{ t('name') }}</label>
          <Field
            v-model="values.name"
            name="name"
            type="text"
            class="form-control"
            :class="{ 'is-invalid': errors.name }"
          />
          <div class="invalid-feedback">{{ errors.name }}</div>
        </div>
        <div class="form-group mb-3">
          <button class="btn btn-primary" :disabled="isSubmitting">
            <span v-show="isSubmitting" class="spinner-border spinner-border-sm mr-1"></span>
            {{ t('save') }}
          </button>
        </div>
      </Form>
    </div>
  </div>
  <div class="card mt-3 border border-danger">
    <h4 class="card-header text-danger">{{ t('delete_organization_header') }}</h4>
    <div class="card-body">
      <p>{{ t('delete_organization_body') }}</p>
      <button :disabled="shouldDisableDeleteButton" @click="onDelete" class="btn btn-danger">
        {{ t('delete') }}
      </button>
      <label v-if="shouldDisableDeleteButton" class="ms-2">{{ t('cancellable_help') }}</label>
    </div>
  </div>
</template>
